// src/llmConnections/api/llmConnectionsApi.js
//
// Real LLM connections API — calls
// presentation/shared/connections/connections_controller.py's /llm routes.
// Separate from connectors/api/connectorsApi.js (source control/work item)
// because the request/response shape genuinely differs: every LLM
// connection carries a `level` (org/account/project/personal) plus,
// depending on level, an `account_id` or `engagement_id`.
//
// list_llm_connections has no permission gate at all (any authenticated
// user asks "what's visible to me") — the backend's own SQL filter
// (build_visible_scope_filter) already returns exactly: every org-wide row
// + every account-scoped row on an account you can reach + every
// project-scoped row on a project you can reach + your own personal rows.
// Nothing to filter client-side.
//
// register_llm_connection has no permission gate when level === "personal";
// otherwise it requires connections:write, checked at the org, account, or
// project scope matching `level` (RegisterLlmConnectionModal only offers
// the levels the caller actually holds that permission for).
//
// There is no deactivate route for LLM connections either — same as
// source-control/work-item, LlmConnectionCard disables that action.

import { apiGet, apiPost } from "../../api/client";

function toConnection(dto) {
  return {
    id: dto.connection_id,
    name: dto.name,
    provider: dto.provider,
    purpose: dto.purpose,
    level: dto.level,
    accountId: dto.account_id,
    engagementId: dto.engagement_id,
    status: dto.is_active ? "active" : "untested",
    lastTestedAt: null,
    lastError: undefined,
  };
}

function buildRegisterPayload({ level, accountId, engagementId, provider, values }) {
  const base = {
    name: values.name,
    provider,
    purpose: values.purpose,
    level,
    account_id: level === "account" ? accountId : null,
    engagement_id: level === "project" ? engagementId : null,
  };

  if (provider === "ollama") {
    // Matches the backend's own auth priority (see
    // infrastructure/shared/platform/ai_capability/*_client_factory.py's
    // Ollama builders): api_key wins as a bearer header when authType is
    // "api_key"; username/password (HTTP basic) only apply when authType
    // is "basic"; "none"/unset (the Select's unset default) sends neither.
    const config = {
      base_url: values.baseUrl,
      model: values.model,
      verify_ssl: values.verifySsl !== "false",
    };
    let apiKey = "";
    if (values.authType === "api_key") {
      apiKey = values.apiKey || "";
    } else if (values.authType === "basic") {
      config.username = values.username;
      config.password = values.password;
    }
    return { ...base, api_key: apiKey, config };
  }
  // azure_openai
  return {
    ...base,
    api_key: values.apiKey,
    config: {
      endpoint: values.endpoint,
      deployment: values.deployment,
      ...(values.apiVersion ? { api_version: values.apiVersion } : {}),
    },
  };
}

export async function listConnections() {
  const dtos = await apiGet("/v1/connections/llm");
  return (dtos || []).map(toConnection);
}

export async function registerConnection({ level, accountId, engagementId, provider, values }) {
  const body = buildRegisterPayload({ level, accountId, engagementId, provider, values });
  const dto = await apiPost("/v1/connections/llm", body);
  const testResult = await testConnectionRaw(dto.connection_id);
  return {
    connection: toConnection(dto),
    testResult: { status: testResult.success ? "passed" : "failed", message: testResult.message },
  };
}

async function testConnectionRaw(id) {
  return apiPost(`/v1/connections/llm/${id}/test`);
}

export async function testConnection(id) {
  const result = await testConnectionRaw(id);
  return { status: result.success ? "passed" : "failed", message: result.message };
}
